/**
 * Pai Gow Poker — server-authoritative rules engine (2–6 players against the dealer, session game with chips).
 *
 * Rules implemented:
 *  - A 53-card deck: the standard 52 plus one joker, reshuffled before every round. Each player places one bet, then every player and
 *    the dealer get seven cards.
 *  - The joker is a bug: it counts as an ace, or as any card that completes a straight, a flush, a straight flush or a royal flush.
 *    With the four natural aces it makes five aces, the best five-card hand.
 *  - Each player splits the seven cards into a five-card high hand and a two-card low hand. The high hand must outrank the low hand
 *    (a pair in the low hand needs a higher pair or better above it, a high-card low hand needs a higher high-card hand or better).
 *    A split that breaks this is refused rather than played as a foul, so a bad tap never costs a bet.
 *  - Five-card hands rank as in poker, with one exception: A-2-3-4-5 is the second-highest straight (above K-Q-J-10-9, below
 *    A-K-Q-J-10). The two-card hand is a pair or two high cards.
 *  - A player may ask for the house way, which sets the hands with the same fixed heuristic the dealer uses (see {@link paiGowHouseWay}).
 *  - Winning both hands pays 1:1 minus a commission (5% by default) on the winnings; winning one and losing one is a push; losing both
 *    loses the bet. Tied (copy) hands go to the dealer, so a hand only wins by beating the dealer's.
 */
import { buildDeck, parseCard, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type SeatSetup,
} from '../../core/types.js';

export const PAI_GOW_JOKER: CardId = 'JK:0';

export type PaiGowCategory =
  | 'HIGH_CARD'
  | 'PAIR'
  | 'TWO_PAIR'
  | 'THREE_OF_A_KIND'
  | 'STRAIGHT'
  | 'FLUSH'
  | 'FULL_HOUSE'
  | 'FOUR_OF_A_KIND'
  | 'STRAIGHT_FLUSH'
  | 'ROYAL_FLUSH'
  | 'FIVE_ACES';

const CATEGORY_RANK: Record<PaiGowCategory, number> = {
  HIGH_CARD: 1,
  PAIR: 2,
  TWO_PAIR: 3,
  THREE_OF_A_KIND: 4,
  STRAIGHT: 5,
  FLUSH: 6,
  FULL_HOUSE: 7,
  FOUR_OF_A_KIND: 8,
  STRAIGHT_FLUSH: 9,
  ROYAL_FLUSH: 10,
  FIVE_ACES: 11,
};

export interface PaiGowHandValue {
  category: PaiGowCategory;
  /** Totally ordered strength on one scale for five-card and two-card hands; higher wins. */
  value: number;
}

/** Five slots of base 32 (ranks go up to 28 for the doubled straight scale) under the category. */
const pack = (cat: number, ...ks: number[]): number => {
  let v = cat;
  for (let i = 0; i < 5; i++) v = v * 32 + (ks[i] ?? 0);
  return v;
};

const isJoker = (id: CardId): boolean => parseCard(id).joker;
const rankOf = (id: CardId): number => (isJoker(id) ? 14 : parseCard(id).rank);

/** Evaluates five cards that are all real (no joker). */
function evaluateNatural(cards: readonly { rank: number; suit: Suit }[]): PaiGowHandValue {
  const ranks = cards.map((c) => c.rank).sort((a, b) => b - a);
  const flush = cards.every((c) => c.suit === cards[0]!.suit);
  const counts = new Map<number, number>();
  for (const r of ranks) counts.set(r, (counts.get(r) ?? 0) + 1);
  const groups = [...counts.entries()].sort((a, b) => b[1] - a[1] || b[0] - a[0]);
  const distinct = groups.length === 5;
  let straight: number | null = null;
  if (distinct && ranks[0]! - ranks[4]! === 4) straight = ranks[0]! * 2;
  // A-2-3-4-5 is the second-highest straight: between K-high (26) and A-high (28).
  else if (distinct && ranks.join() === '14,5,4,3,2') straight = 27;
  const make = (cat: PaiGowCategory, ...ks: number[]): PaiGowHandValue => ({ category: cat, value: pack(CATEGORY_RANK[cat], ...ks) });
  if (straight !== null && flush) return straight === 28 ? make('ROYAL_FLUSH') : make('STRAIGHT_FLUSH', straight);
  const top = groups[0]!;
  if (top[1] === 4) return make('FOUR_OF_A_KIND', top[0], groups[1]![0]);
  if (top[1] === 3 && groups[1]![1] === 2) return make('FULL_HOUSE', top[0], groups[1]![0]);
  if (flush) return make('FLUSH', ...ranks);
  if (straight !== null) return make('STRAIGHT', straight);
  if (top[1] === 3) return make('THREE_OF_A_KIND', top[0], groups[1]![0], groups[2]![0]);
  if (top[1] === 2 && groups[1]![1] === 2) return make('TWO_PAIR', top[0], groups[1]![0], groups[2]![0]);
  if (top[1] === 2) return make('PAIR', top[0], groups[1]![0], groups[2]![0], groups[3]![0]);
  return make('HIGH_CARD', ...ranks);
}

const FULL_DECK: readonly CardId[] = buildDeck();
const FIVE_CATEGORIES_FOR_BUG = new Set<PaiGowCategory>(['STRAIGHT', 'FLUSH', 'STRAIGHT_FLUSH', 'ROYAL_FLUSH']);

/** Best five-card value for five cards that may include the joker. */
export function paiGowEvaluateFive(cards: readonly CardId[]): PaiGowHandValue {
  if (cards.length !== 5) throw new Error('A Pai Gow high hand has exactly 5 cards');
  const real = cards.filter((c) => !isJoker(c));
  if (real.length === 5) return evaluateNatural(real.map(parseCard));
  if (real.length === 4 && real.every((c) => parseCard(c).rank === 14) && new Set(real).size === 4) {
    return { category: 'FIVE_ACES', value: pack(CATEGORY_RANK.FIVE_ACES) };
  }
  let best: PaiGowHandValue | null = null;
  const have = new Set(real);
  for (const sub of FULL_DECK) {
    if (have.has(sub)) continue;
    const card = parseCard(sub);
    const v = evaluateNatural([...real.map(parseCard), card]);
    // The bug is an ace, or a card that completes a straight, flush, straight flush or royal flush.
    if (card.rank === 14 || FIVE_CATEGORIES_FOR_BUG.has(v.category)) {
      if (best === null || v.value > best.value) best = v;
    }
  }
  return best!;
}

/** The two-card hand: a pair or two high cards (the joker is an ace). Same scale as the five-card hands. */
export function paiGowEvaluateTwo(cards: readonly CardId[]): PaiGowHandValue {
  if (cards.length !== 2) throw new Error('A Pai Gow low hand has exactly 2 cards');
  const [a, b] = cards.map(rankOf).sort((x, y) => y - x) as [number, number];
  if (a === b) return { category: 'PAIR', value: pack(CATEGORY_RANK.PAIR, a) };
  return { category: 'HIGH_CARD', value: pack(CATEGORY_RANK.HIGH_CARD, a, b) };
}

/** The high hand must outrank the low hand (a high hand with the same leading cards wins on its extra cards). */
export function paiGowIsLegalSplit(high: readonly CardId[], low: readonly CardId[]): boolean {
  return paiGowEvaluateFive(high).value > paiGowEvaluateTwo(low).value;
}

export interface PaiGowSplit {
  high: CardId[];
  low: CardId[];
}

const CATEGORY_POINTS: Record<PaiGowCategory, number> = {
  HIGH_CARD: 0,
  PAIR: 18,
  TWO_PAIR: 48,
  THREE_OF_A_KIND: 55,
  STRAIGHT: 62,
  FLUSH: 66,
  FULL_HOUSE: 75,
  FOUR_OF_A_KIND: 85,
  STRAIGHT_FLUSH: 90,
  ROYAL_FLUSH: 95,
  FIVE_ACES: 100,
};

/** How good a split is under the house way: category points plus the leading rank for the high hand, pair or high cards for the low. */
function splitScore(high: readonly CardId[], low: readonly CardId[]): number {
  const h = paiGowEvaluateFive(high);
  const l = paiGowEvaluateTwo(low);
  const lowRanks = low.map(rankOf).sort((a, b) => b - a) as [number, number];
  const highTop = Math.max(...high.map(rankOf));
  const highScore = CATEGORY_POINTS[h.category] + (highTop / 14) * 8;
  const lowScore = l.category === 'PAIR' ? 20 + lowRanks[0] * 2 : lowRanks[0] * 1.6 + lowRanks[1] * 0.6;
  return highScore + lowScore;
}

/**
 * The house way: of the 21 ways to pick the two-card hand, take the legal split with the best points score (high-hand category and top
 * card plus the low-hand pair or high cards). It is the dealer's fixed rule and also what "house way" sets for a player and what the
 * bots play. It is a heuristic of this game, not a casino's published table. Ties keep the split with the higher five-card hand.
 */
export function paiGowHouseWay(cards: readonly CardId[]): PaiGowSplit {
  if (cards.length !== 7) throw new Error('Pai Gow Poker is played with exactly 7 cards');
  let best: { split: PaiGowSplit; score: number; high: number } | null = null;
  for (let i = 0; i < 7; i++) {
    for (let j = i + 1; j < 7; j++) {
      const low = [cards[i]!, cards[j]!];
      const high = cards.filter((_, k) => k !== i && k !== j);
      if (!paiGowIsLegalSplit(high, low)) continue;
      const score = splitScore(high, low);
      const hv = paiGowEvaluateFive(high).value;
      if (best === null || score > best.score || (score === best.score && hv > best.high)) best = { split: { high, low }, score, high: hv };
    }
  }
  // Seven cards always allow at least one legal split; keep a sane fallback anyway.
  return best ? orderSplit(best.split) : orderSplit({ high: cards.slice(2), low: cards.slice(0, 2) });
}

const byRankDesc = (a: CardId, b: CardId): number => rankOf(b) - rankOf(a);
function orderSplit(s: PaiGowSplit): PaiGowSplit {
  return { high: [...s.high].sort(byRankDesc), low: [...s.low].sort(byRankDesc) };
}

export interface PaiGowConfig {
  minBet: number;
  maxBet: number;
  minPlayers: number;
  maxPlayers: number;
  /** Percent of the winnings kept by the house when both hands win. */
  commissionPercent: number;
  startDelayMs: number;
  dealDelayMs: number;
  handEndDelayMs: number;
}

export type PaiGowPhase = 'WAITING' | 'BETTING' | 'DEALING' | 'SETTING' | 'REVEAL' | 'HAND_END';
export type PaiGowSeatStatus = 'WAITING' | 'BET' | 'SIT_OUT';
export type PaiGowOutcome = 'WIN' | 'LOSE' | 'PUSH';

export interface PaiGowSeat {
  playerId: string;
  chips: number;
  status: PaiGowSeatStatus;
  bet: number;
  cards: CardId[];
  /** The hands once the player has set them, otherwise null. */
  set: PaiGowSplit | null;
  /** What came back at the end of the round (stake plus winnings, or 0). */
  payout: number;
}

export interface PaiGowResultSeat {
  seat: number;
  bet: number;
  outcome: PaiGowOutcome;
  highWon: boolean;
  lowWon: boolean;
  high: CardId[];
  low: CardId[];
  highCategory: PaiGowCategory;
  lowCategory: PaiGowCategory;
  commission: number;
  payout: number;
  delta: number;
}

export interface PaiGowResult {
  handNo: number;
  dealerHigh: CardId[];
  dealerLow: CardId[];
  dealerHighCategory: PaiGowCategory;
  dealerLowCategory: PaiGowCategory;
  seats: PaiGowResultSeat[];
}

export interface PaiGowState {
  config: PaiGowConfig;
  seats: (PaiGowSeat | null)[];
  phase: PaiGowPhase;
  handNo: number;
  /** Seat to bet or to set its hands. */
  turn: number;
  deck: CardId[];
  dealer: CardId[];
  lastResult: PaiGowResult | null;
  handsPlayed: number;
}

export type PaiGowAction = { type: 'bet'; amount: number } | { type: 'sitOut' } | { type: 'set'; low: CardId[] } | { type: 'houseWay' };

export type PaiGowLegal = { kind: 'bet'; min: number; max: number; canSitOut: boolean } | { kind: 'set' } | { kind: 'none' };

export interface PaiGowViewSeat {
  seat: number;
  playerId: string;
  chips: number;
  status: PaiGowSeatStatus;
  bet: number;
  hasCards: boolean;
  /** True once the player has set their hands (the hands themselves stay hidden until the reveal). */
  isSet: boolean;
  payout: number;
}

export interface PaiGowView {
  gameType: 'PAI_GOW';
  config: PaiGowConfig;
  seat: number | null;
  phase: PaiGowPhase;
  handNo: number;
  turn: number;
  /** My own seven cards (empty before the deal or when I am not in the round). */
  hand: CardId[];
  /** My hands once set. */
  mySet: PaiGowSplit | null;
  /** The house-way split of my cards, offered as a starting point while I set. */
  suggested: PaiGowSplit | null;
  deckCount: number;
  players: (PaiGowViewSeat | null)[];
  lastResult: PaiGowResult | null;
}

const DEFAULTS: PaiGowConfig = {
  minBet: 10,
  maxBet: 1000,
  minPlayers: 2,
  maxPlayers: 6,
  commissionPercent: 5,
  startDelayMs: 2000,
  dealDelayMs: 1500,
  handEndDelayMs: 5000,
};

const freshDeck = (rng: Rng): CardId[] => shuffle([...buildDeck(), PAI_GOW_JOKER], rng);

function seatsWhere(s: PaiGowState, pred: (p: PaiGowSeat, i: number) => boolean): number[] {
  const out: number[] = [];
  s.seats.forEach((p, i) => {
    if (p && pred(p, i)) out.push(i);
  });
  return out;
}

const canJoin = (s: PaiGowState, p: PaiGowSeat): boolean => p.chips >= s.config.minBet;
const canStart = (s: PaiGowState): boolean => seatsWhere(s, (p) => canJoin(s, p)).length >= s.config.minPlayers;

function advanceBetting(s: PaiGowState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'WAITING') {
      s.turn = seat;
      return;
    }
  }
  s.turn = -1;
  s.phase = seatsWhere(s, (p) => p.status === 'BET').length > 0 ? 'DEALING' : 'WAITING';
}

function advanceSetting(s: PaiGowState, from: number): void {
  for (let seat = from + 1; seat < s.seats.length; seat++) {
    const p = s.seats[seat];
    if (p && p.status === 'BET' && p.set === null) {
      s.turn = seat;
      return;
    }
  }
  s.turn = -1;
  s.phase = 'REVEAL';
}

function startHand(s: PaiGowState, rng: Rng, events: EngineEvent[]): void {
  s.handNo++;
  s.deck = freshDeck(rng);
  s.dealer = [];
  for (const p of s.seats) {
    if (!p) continue;
    p.bet = 0;
    p.cards = [];
    p.set = null;
    p.payout = 0;
    p.status = canJoin(s, p) ? 'WAITING' : 'SIT_OUT';
  }
  s.phase = 'BETTING';
  s.turn = -1;
  events.push({ type: 'handStart', handNo: s.handNo });
  advanceBetting(s, -1);
  if (s.phase !== 'BETTING') events.push({ type: 'waitingForPlayers' });
}

function settle(s: PaiGowState, events: EngineEvent[]): void {
  const dealerSplit = paiGowHouseWay(s.dealer);
  const dHigh = paiGowEvaluateFive(dealerSplit.high);
  const dLow = paiGowEvaluateTwo(dealerSplit.low);
  const seats: PaiGowResultSeat[] = [];
  s.seats.forEach((p, seat) => {
    if (!p || p.status !== 'BET') return;
    // A player who somehow never set their hands is set the house way.
    const split = p.set ?? paiGowHouseWay(p.cards);
    const high = paiGowEvaluateFive(split.high);
    const low = paiGowEvaluateTwo(split.low);
    // Copy (equal) hands go to the dealer.
    const highWon = high.value > dHigh.value;
    const lowWon = low.value > dLow.value;
    let payout = 0;
    let commission = 0;
    let outcome: PaiGowOutcome;
    if (highWon && lowWon) {
      outcome = 'WIN';
      commission = Math.round((p.bet * s.config.commissionPercent) / 100);
      payout = p.bet * 2 - commission;
    } else if (highWon || lowWon) {
      outcome = 'PUSH';
      payout = p.bet;
    } else {
      outcome = 'LOSE';
    }
    p.payout = payout;
    p.chips += payout;
    seats.push({
      seat,
      bet: p.bet,
      outcome,
      highWon,
      lowWon,
      high: [...split.high],
      low: [...split.low],
      highCategory: high.category,
      lowCategory: low.category,
      commission,
      payout,
      delta: payout - p.bet,
    });
  });
  s.lastResult = {
    handNo: s.handNo,
    dealerHigh: [...dealerSplit.high],
    dealerLow: [...dealerSplit.low],
    dealerHighCategory: dHigh.category,
    dealerLowCategory: dLow.category,
    seats,
  };
  s.handsPlayed++;
  s.phase = 'HAND_END';
  events.push({ type: 'handEnd', handNo: s.handNo, results: seats.map((r) => ({ seat: r.seat, delta: r.delta })) });
}

export const paiGowEngine: GameEngine<PaiGowConfig, PaiGowState, PaiGowAction, PaiGowView, PaiGowLegal> = {
  gameType: 'PAI_GOW',
  kind: 'session',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    // Seven cards each for every player plus the dealer from 53 cards: at most six players.
    checkInt('maxPlayers', c.maxPlayers, 1, 6);
    checkInt('minPlayers', c.minPlayers, 1, c.maxPlayers);
    checkInt('minBet', c.minBet, 1, 1e9);
    checkInt('maxBet', c.maxBet, c.minBet, 1e12);
    checkInt('commissionPercent', c.commissionPercent, 0, 20);
    checkInt('startDelayMs', c.startDelayMs, 0, 60000);
    checkInt('dealDelayMs', c.dealDelayMs, 0, 60000);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: (c) => ({ min: c.minPlayers, max: c.maxPlayers }),

  createState(config, seats, rng) {
    const s: PaiGowState = {
      config,
      seats: Array<PaiGowSeat | null>(config.maxPlayers).fill(null),
      phase: 'WAITING',
      handNo: 0,
      turn: -1,
      deck: freshDeck(rng),
      dealer: [],
      lastResult: null,
      handsPlayed: 0,
    };
    for (const setup of seats) this.addSeat!(s, setup);
    return s;
  },

  addSeat(s, setup: SeatSetup) {
    assertEngine(setup.seat >= 0 && setup.seat < s.seats.length && !s.seats[setup.seat], 'SEAT_TAKEN', 'Seat unavailable');
    const chips = setup.chips ?? 0;
    assertEngine(Number.isInteger(chips) && chips >= 0, 'INVALID_AMOUNT', 'Invalid chips');
    // While bets are still being taken a later seat can join in; once the cards are coming they wait.
    const open = s.phase === 'WAITING' || s.phase === 'HAND_END' || (s.phase === 'BETTING' && chips >= s.config.minBet);
    s.seats[setup.seat] = { playerId: setup.playerId, chips, status: open ? 'WAITING' : 'SIT_OUT', bet: 0, cards: [], set: null, payout: 0 };
    return [{ type: 'playerJoined', seat: setup.seat, playerId: setup.playerId, chips }];
  },

  removeSeat(s, seat) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'Seat empty');
    let chips = p.chips;
    const wasTurn = s.turn === seat;
    if (s.phase === 'BETTING') {
      // Nothing is dealt yet: the bet comes back.
      chips += p.bet;
      s.seats[seat] = null;
      if (wasTurn) advanceBetting(s, seat);
    } else {
      // Leaving once the cards are out forfeits the bet on the table.
      s.seats[seat] = null;
      if (wasTurn && s.phase === 'SETTING') advanceSetting(s, seat);
    }
    return { chips, events: [{ type: 'playerLeft', seat }] };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'sitOut' || raw.type === 'houseWay') return { type: raw.type };
    if (raw.type === 'bet' && typeof raw.amount === 'number' && Number.isInteger(raw.amount)) return { type: 'bet', amount: raw.amount };
    if (raw.type === 'set' && Array.isArray(raw.low) && raw.low.length === 2 && raw.low.every((c) => typeof c === 'string')) {
      return { type: 'set', low: [raw.low[0] as string, raw.low[1] as string] };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Pai Gow Poker action');
  },

  applyAction(s, seat, action) {
    const p = s.seats[seat];
    assertEngine(p, 'INVALID_ACTION', 'You are not at this table');
    assertEngine(s.phase === 'BETTING' || s.phase === 'SETTING', 'INVALID_PHASE', 'Not your time to act');
    assertEngine(seat === s.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    if (s.phase === 'BETTING') {
      assertEngine(action.type === 'bet' || action.type === 'sitOut', 'INVALID_PHASE', 'Place your bet first');
      if (action.type === 'sitOut') {
        p.status = 'SIT_OUT';
        events.push({ type: 'sitOut', seat });
        advanceBetting(s, seat);
        return events;
      }
      const max = Math.min(s.config.maxBet, p.chips);
      assertEngine(action.amount >= s.config.minBet && action.amount <= max, 'INVALID_AMOUNT', `Bet between ${s.config.minBet} and ${max}`);
      p.chips -= action.amount;
      p.bet = action.amount;
      p.status = 'BET';
      events.push({ type: 'bet', seat, amount: action.amount });
      advanceBetting(s, seat);
      return events;
    }
    assertEngine(action.type === 'set' || action.type === 'houseWay', 'INVALID_PHASE', 'Set your hands');
    if (action.type === 'houseWay') {
      p.set = paiGowHouseWay(p.cards);
    } else {
      const a = action.low[0] as CardId;
      const b = action.low[1] as CardId;
      assertEngine(a !== b && p.cards.includes(a) && p.cards.includes(b), 'INVALID_ACTION', 'The low hand must be two of your own cards');
      const low = [a, b];
      const high = p.cards.filter((c) => c !== a && c !== b);
      assertEngine(paiGowIsLegalSplit(high, low), 'INVALID_ACTION', 'The five-card hand must beat the two-card hand');
      p.set = orderSplit({ high, low });
    }
    events.push({ type: 'handsSet', seat });
    advanceSetting(s, seat);
    return events;
  },

  getActors(s) {
    return (s.phase === 'BETTING' || s.phase === 'SETTING') && s.turn >= 0 && s.seats[s.turn] ? [s.turn] : [];
  },

  getLegalActions(s, seat) {
    const p = s.seats[seat];
    if (!p || seat !== s.turn) return { kind: 'none' };
    if (s.phase === 'BETTING') return { kind: 'bet', min: s.config.minBet, max: Math.min(s.config.maxBet, p.chips), canSitOut: true };
    if (s.phase === 'SETTING') return { kind: 'set' };
    return { kind: 'none' };
  },

  getTimeoutAction(s, seat) {
    // A player who cannot be reached after the deal is set the house way; before it they sit the round out.
    return s.phase === 'SETTING' && s.turn === seat ? { type: 'houseWay' } : { type: 'sitOut' };
  },

  getView(s, seat) {
    const me = seat !== null ? s.seats[seat] : null;
    const hand = me && me.status === 'BET' ? [...me.cards] : [];
    return {
      gameType: 'PAI_GOW',
      config: s.config,
      seat,
      phase: s.phase,
      handNo: s.handNo,
      turn: s.turn,
      hand,
      mySet: me?.set ? { high: [...me.set.high], low: [...me.set.low] } : null,
      suggested: hand.length === 7 && s.phase === 'SETTING' ? paiGowHouseWay(hand) : null,
      deckCount: s.deck.length,
      players: s.seats.map((p, i) =>
        p ? { seat: i, playerId: p.playerId, chips: p.chips, status: p.status, bet: p.bet, hasCards: p.cards.length > 0, isSet: p.set !== null, payout: p.payout } : null,
      ),
      lastResult: s.lastResult ? structuredClone(s.lastResult) : null,
    };
  },

  getSystemStep(s) {
    if (s.phase === 'DEALING') return { kind: 'deal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'REVEAL') return { kind: 'reveal', delayMs: s.config.dealDelayMs };
    if (s.phase === 'HAND_END') return { kind: 'nextHand', delayMs: s.config.handEndDelayMs };
    if (s.phase === 'WAITING' && canStart(s)) return { kind: 'startHand', delayMs: s.config.startDelayMs };
    return null;
  },

  runSystemStep(s, rng) {
    const events: EngineEvent[] = [];
    if (s.phase === 'DEALING') {
      const players = seatsWhere(s, (p) => p.status === 'BET');
      if (s.deck.length < (players.length + 1) * 7) s.deck = freshDeck(rng);
      for (const seat of players) s.seats[seat]!.cards = Array.from({ length: 7 }, () => s.deck.pop()!);
      s.dealer = Array.from({ length: 7 }, () => s.deck.pop()!);
      s.phase = 'SETTING';
      events.push({ type: 'cardsDealt', seats: players });
      advanceSetting(s, -1);
      return events;
    }
    if (s.phase === 'REVEAL') {
      settle(s, events);
      return events;
    }
    if (s.phase === 'HAND_END' || s.phase === 'WAITING') {
      s.phase = 'WAITING';
      if (canStart(s)) startHand(s, rng, events);
      else events.push({ type: 'waitingForPlayers' });
    }
    return events;
  },

  isFinished: () => false,
  isHandInProgress: (s) => s.phase === 'DEALING' || s.phase === 'SETTING' || s.phase === 'REVEAL',
  getChips: (s) => Object.fromEntries(seatsWhere(s, () => true).map((i) => [i, s.seats[i]!.chips])),
  getStandings: (s) => standingsFromScores(seatsWhere(s, () => true).map((seat) => ({ seat, score: s.seats[seat]!.chips }))),
  getRoundsCompleted: (s) => s.handsPlayed,
};
